import React, { useEffect, useState, useRef } from "react";
import { fetchAnnouncements } from "@/services/eventApi";
import { useAppContext } from "@/context/Context";
import Link from "next/link";
import Image from "next/image";
import styles from "./Announce.module.css";

const TruncatedTitle = ({
  title,
  as = "h3",
  className = "",
  style = {},
  maxLines = 3,
}) => {
  const Tag = as;
  const textRef = useRef(null);
  const [displayText, setDisplayText] = useState(title);

  useEffect(() => {
    if (!textRef.current) return;
    const element = textRef.current;
    // Get actual computed line-height in pixels
    let computedLineHeight = parseFloat(getComputedStyle(element).lineHeight);
    // If line-height is "normal", fallback to font-size * 1.2
    if (isNaN(computedLineHeight)) {
      computedLineHeight = parseFloat(getComputedStyle(element).fontSize) * 1.2;
    }
    const maxHeight = computedLineHeight * maxLines;
    element.textContent = title;
    if (element.scrollHeight > maxHeight) {
      let low = 0;
      let high = title.length;
      while (low < high) {
        const mid = Math.floor((low + high + 1) / 2);
        element.textContent = title.slice(0, mid) + "...";
        if (element.scrollHeight <= maxHeight) {
          low = mid;
        } else {
          high = mid - 1;
        }
      }
      setDisplayText(title.slice(0, low) + "...");
    } else {
      setDisplayText(title);
    }
  }, [title, maxLines]);

  return (
    <Tag
      ref={textRef}
      className={className}
      style={{
        ...style,
        overflow: "hidden",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: maxLines,
        lineHeight: "1.4",
        whiteSpace: "normal",
      }}
      title={title}
    >
      {displayText}
    </Tag>
  );
};

function Announce() {
  const [announcements, setAnnouncements] = useState([]);
  const [error, setError] = useState(null);
  const { announceOpen, setAnnounceOpen } = useAppContext();
  const [isMinimized, setIsMinimized] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const scrollRef = useRef(null);
  const scrollInterval = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.matchMedia("(max-width: 767px)").matches);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchAnnouncements()
      .then((data) => {
        if (Array.isArray(data)) {
          setAnnouncements(data);
        } else if (data && Array.isArray(data.announcements)) {
          setAnnouncements(data.announcements);
        } else {
          setAnnouncements([]);
        }
      })
      .catch((err) => {
        setError("Failed to load announcements");
        console.error(err);
      });
  }, []);

  useEffect(() => {
    if (announcements.length > 0 && scrollRef.current && !isMinimized) {
      startAutoScroll();
    }
    return () => stopAutoScroll();
  }, [announcements, isMinimized]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsMinimized(true);
        setScrolled(true);
      } else {
        setScrolled(false);
        setIsMinimized(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const startAutoScroll = () => {
    if (scrollInterval.current) return;
    scrollInterval.current = setInterval(() => {
      const el = scrollRef.current;
      if (el) {
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 2) {
          el.scrollTop = 0;
        } else {
          el.scrollTop += 1;
        }
      }
    }, 15);
  };

  const stopAutoScroll = () => {
    if (scrollInterval.current) {
      clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    }
  };

  if (!announceOpen) return null;

  if (isMobile) {
    return (
      <div
        className="container"
        style={{
          position: "fixed",
          top: "80px",
          right: "10px",
          width: "300px",
          maxWidth: "calc(100vw - 20px)",
          maxHeight: "calc(100vh - 100px)",
          zIndex: 3000,
          backgroundColor: "dimgray",
          borderRadius: "12px",
          padding: "10px",
          boxShadow: "0 0 15px rgba(0,0,0,0.5)",
          display: "flex",
          flexDirection: "column",
          transition: "background-color 0.3s ease",
        }}
      >
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2 border-white border-opacity-50 flex-shrink-0">
          <h2
            className="h4 fw-bold mb-0"
            style={{
              fontSize: "1.5rem",
              color: "#A259E0",
            }}
          >
            📢 New Events
          </h2>
          <div className="d-flex align-items-center gap-2">
            <Link
              href="/announcements"
              className="rbt-switch-btn btn-gradient btn-sm hover-transform-none"
              style={{
                padding: "4px 10px",
                borderRadius: "8px",
                fontSize: "11px",
                color: "white",
                display: "inline-block",
                whiteSpace: "nowrap",
              }}
            >
              View All
            </Link>
            <button
              onClick={() => setAnnounceOpen(false)}
              style={{
                backgroundColor: "transparent",
                border: "none",
                color: "white",
                fontSize: "1.5rem",
                cursor: "pointer",
                padding: 0,
                userSelect: "none",
                lineHeight: 1,
              }}
              aria-label="Close Announcements"
              title="Hide Announcements"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Body */}
        <aside
          className="rounded-4 text-white"
          style={{
            flex: 1,
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            width: "100%",
          }}
          aria-label="Upcoming Events and Announcements"
          ref={scrollRef}
          onMouseEnter={stopAutoScroll}
          onMouseLeave={startAutoScroll}
        >
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          {!error && announcements.length === 0 && (
            <div
              className="text-center text-light"
              style={{ fontSize: "1.6rem" }}
            >
              Loading announcements...
            </div>
          )}
          <div>
            {announcements.map((item, idx) => (
              <article
                key={item._id || idx}
                className="mb-4 rounded-3 shadow-sm d-flex flex-column justify-content-between"
                style={{
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  minHeight: "200px",
                  maxHeight: "260px",
                  padding: "20px 14px",
                  fontSize: "1.1rem",
                  color: "yellow",
                }}
              >
                {/* Mobile Title with Truncation */}
                <TruncatedTitle
                  title={item.title}
                  as="h4"
                  className="rbt-card-title"
                  style={{ marginBottom: "12px", color: "white" }}
                  maxLines={3}
                />

                <ul className="list-unstyled mb-4">
                  <li className="mb-2 d-flex" style={{ color: "#FFF" }}>
                    <div className="flex-shrink-0">
                      <i className="bi bi-geo-alt-fill text-primary"></i>&nbsp;
                      <strong className="text-uppercase">Venue:</strong>&nbsp;
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                      }}
                      title={item.venue}
                    >
                      {item.venue}
                    </div>
                  </li>
                  <li className="mb-2 d-flex" style={{ color: "#FFF" }}>
                    <div className="flex-shrink-0">
                      <i className="bi bi-calendar-event text-danger"></i>&nbsp;
                      <strong className="text-uppercase">Dates:</strong>&nbsp;
                    </div>
                    <div className="flex-grow-1">
                      {item.fromDate} to {item.toDate}
                    </div>
                  </li>
                  <li className="mb-2 d-flex" style={{ color: "#FFF" }}>
                    <div className="flex-shrink-0">
                      <i className="bi bi-clock-fill text-warning"></i>&nbsp;
                      <strong className="text-uppercase">Time:</strong>&nbsp;
                    </div>
                    <div className="flex-grow-1">
                      {item.timeFrom} - {item.timeTo}
                    </div>
                  </li>
                  <li className="d-flex" style={{ color: "#FFF" }}>
                    <div className="flex-shrink-0">
                      <i className="bi bi-mortarboard-fill text-info"></i>&nbsp;
                      <strong className="text-uppercase">Skill Level:</strong>
                      &nbsp;
                    </div>
                    <div className="flex-grow-1">{item.skillLevel}</div>
                  </li>
                </ul>
                <div className="mt-auto d-flex justify-content-end">
                  {item.amount && item.amount > 0 ? (
                    <Image
                      src="/MainHome/paid.png"
                      width={60}
                      height={60}
                      alt="Paid"
                      priority
                      style={{ marginRight: "10px" }}
                    />
                  ) : (
                    <Image
                      src="/MainHome/free.png"
                      width={60}
                      height={60}
                      alt="Free"
                      priority
                      style={{ marginRight: "10px" }}
                    />
                  )}
                  <Link
                    className="rbt-btn rbt-switch-btn btn-gradient btn-sm"
                    href={`event-details/${item._id}`}
                  >
                    <span data-text="Book Now">Book Now</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </aside>
      </div>
    );
  }

  return (
    <div
      className="container"
      style={{
        maxWidth: "96vw",
        backgroundColor: "dimgray",
        borderRadius: "12px",
        padding: "10px",
        transition: "background-color 0.3s ease",
        marginRight: "20px",
      }}
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2 border-white border-opacity-50">
        <h2 className={styles.headerTitle}>
          <span className={styles.icon}>📢</span> New Events
        </h2>
        <div className={styles.actions}>
          <Link href="/announcements" className={styles.viewAll}>
            View All
          </Link>
          <button
            onClick={() => setAnnounceOpen(false)}
            className={styles.closeBtn}
            aria-label="Close Announcements"
            title="Close Announcements"
          >
            &times;
          </button>
        </div>
      </div>
      <aside
        className="rounded-4 text-white"
        style={{
          height: "calc(100vh - 220px)",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          width: "300px",
          marginLeft: "15px",
        }}
        aria-label="Upcoming Events and Announcements"
        ref={scrollRef}
        onMouseEnter={stopAutoScroll}
        onMouseLeave={startAutoScroll}
      >
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {!error && announcements.length === 0 && (
          <div
            className="text-center text-light"
            style={{ fontSize: "1.6rem" }}
          >
            Loading Announcements....
          </div>
        )}
        <div>
          {announcements.map((item, idx) => (
            <article
              key={item._id || idx}
              className="mb-4 rounded-3 shadow-sm d-flex flex-column justify-content-between"
              style={{
                border: "1px solid rgba(255, 255, 255, 0.3)",
                minHeight: "280px",
                maxHeight: "360px",
                padding: "28px 24px",
                fontSize: "1.3rem",
                color: "yellow",
              }}
            >
              <TruncatedTitle
                title={item.title}
                as="h3"
                className="fw-bold text-center mb-3"
                style={{ marginBottom: "16px", color: "white" }}
                maxLines={3}
              />

              <ul className="list-unstyled mb-4">
                <li className="mb-2 d-flex" style={{ color: "#FFF" }}>
                  <div className="flex-shrink-0">
                    <i className="bi bi-geo-alt-fill text-primary"></i>&nbsp;
                    <strong className="text-uppercase">Venue:</strong>&nbsp;
                  </div>
                  <div
                    className="flex-grow-1"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      cursor: "pointer",
                    }}
                    title={item.venue}
                  >
                    {item.venue}
                  </div>
                </li>
                <li className="mb-2 d-flex" style={{ color: "#FFF" }}>
                  <div className="flex-shrink-0">
                    <i className="bi bi-calendar-event text-danger"></i>&nbsp;
                    <strong className="text-uppercase">Dates:</strong>&nbsp;
                  </div>
                  <div className="flex-grow-1">
                    {item.fromDate} to {item.toDate}
                  </div>
                </li>
                <li className="mb-2 d-flex" style={{ color: "#FFF" }}>
                  <div className="flex-shrink-0">
                    <i className="bi bi-clock-fill text-warning"></i>&nbsp;
                    <strong className="text-uppercase">Time:</strong>&nbsp;
                  </div>
                  <div className="flex-grow-1">
                    {item.timeFrom} - {item.timeTo}
                  </div>
                </li>
                <li className="d-flex" style={{ color: "#FFF" }}>
                  <div className="flex-shrink-0">
                    <i className="bi bi-mortarboard-fill text-info"></i>&nbsp;
                    <strong className="text-uppercase">Skill Level:</strong>
                    &nbsp;
                  </div>
                  <div className="flex-grow-1">{item.skillLevel}</div>
                </li>
              </ul>
              <div className="mt-auto d-flex justify-content-end">
                {item.amount && item.amount > 0 ? (
                  <Image
                    src="/MainHome/paid.png"
                    width={70}
                    height={70}
                    alt="Paid"
                    priority
                    style={{ marginRight: "25px" }}
                  />
                ) : (
                  <Image
                    src="/MainHome/free.png"
                    width={70}
                    height={70}
                    alt="Free"
                    priority
                    style={{ marginRight: "25px" }}
                  />
                )}
                <Link
                  className="rbt-btn rbt-switch-btn btn-gradient btn-sm"
                  href={`event-details/${item._id}`}
                >
                  <span data-text="Book Now">Book Now</span>
                </Link>
              </div>
            </article>
          ))}
        </div>
      </aside>
    </div>
  );
}

export default Announce;
